// Bulk enrollment-form download: fills every attached carrier/broker form for
// the selected plans, for every employee enrolled in them, and returns a zip.
// Forms that can't be filled (carrier form never analyzed, missing template)
// are listed in SKIPPED.txt inside the zip.

import { useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

export interface BulkFormPlanOption {
  planId: number;
  planName: string;
  employeeCount: number;
}

export function BulkEnrollmentFormsDialog({
  open,
  onOpenChange,
  clientId,
  planOptions,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: number;
  planOptions: BulkFormPlanOption[];
}) {
  const { toast } = useToast();
  const [selected, setSelected] = useState<number[]>([]);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (open) setSelected(planOptions.map((p) => p.planId));
  }, [open, planOptions]);

  const toggle = (planId: number) =>
    setSelected((prev) =>
      prev.includes(planId) ? prev.filter((id) => id !== planId) : [...prev, planId],
    );

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`/api/clients/${clientId}/enrollment-forms/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ planIds: selected }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error || "Failed to generate forms");
      }
      const generated = res.headers.get("X-Generated-Count");
      const skipped = Number(res.headers.get("X-Skipped-Count") ?? 0);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `enrollment-forms-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast({
        title: `${generated ?? "0"} forms generated`,
        description:
          skipped > 0
            ? `${skipped} could not be generated — see SKIPPED.txt in the zip.`
            : undefined,
      });
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Couldn't generate forms",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Download enrollment forms</DialogTitle>
          <DialogDescription>
            One filled PDF per employee, per form attached to the selected plans.
            Signature fields are blank for employees who haven't signed.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[320px] overflow-y-auto space-y-2 py-1">
          {planOptions.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No enrolled plans in this view.
            </p>
          ) : (
            planOptions.map((plan) => (
              <label
                key={plan.planId}
                className="flex items-center gap-3 rounded-md border p-2 cursor-pointer"
              >
                <Checkbox
                  checked={selected.includes(plan.planId)}
                  onCheckedChange={() => toggle(plan.planId)}
                />
                <span className="flex-1 text-sm">{plan.planName}</span>
                <span className="text-xs text-muted-foreground">
                  {plan.employeeCount} enrolled
                </span>
              </label>
            ))
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={generating}>
            Cancel
          </Button>
          <Button onClick={generate} disabled={generating || selected.length === 0}>
            {generating ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-1" />
            )}
            Generate ZIP
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
